import apiClient from "../helpers/apiClient";
import { getStoredToken } from "./auth.service";
import endpoint from "../Endpoint";
import { joinApiUrl } from "../config/api.config";

const API = `/api/v2/ai`;
const api = apiClient();

export const getConversations = (limit = 20) =>
  api.get(`${API}/conversations`, { params: { limit } });

export const getConversation = (id) => api.get(`${API}/conversations/${id}`);

export const deleteConversation = (id) => api.del(`${API}/conversations/${id}`);

export const sendMessage = (message, conversationId = null) =>
  api.post(`${API}/chat`, { message, conversationId });

export async function sendMessageStream(
  message,
  conversationId = null,
  {
    onMeta = () => {},
    onTool = () => {},
    onChunk = () => {},
    onDone = () => {},
    onError = () => {},
  } = {}
) {
  const token = getStoredToken();
  const url = joinApiUrl(endpoint, `${API}/chat/stream`);

  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ message, conversationId }),
  });

  // Log de debug: útil para diagnosticar problemas con el stream SSE
  // eslint-disable-next-line no-console
  console.log("[AI Stream] status:", response.status, "content-type:", response.headers.get("content-type"));

  if (!response.ok) {
    let errorMsg = `HTTP ${response.status}`;
    try {
      const errBody = await response.json();
      errorMsg = errBody.error || errorMsg;
    } catch {
      // ignore
    }
    onError(errorMsg);
    throw new Error(errorMsg);
  }

  const contentType = response.headers.get("content-type") || "";

  // Si el backend devuelve JSON plano (no SSE válido) con status 200,
  // lo parseamos como error para evitar mostrarlo como texto del asistente
  if (contentType.includes("application/json")) {
    try {
      const jsonBody = await response.json();
      console.log("[AI Stream] JSON body:", jsonBody);
      const errorMsg = jsonBody.error || jsonBody.message || "Error inesperado del servidor";
      onError(errorMsg);
      throw new Error(errorMsg);
    } catch (err) {
      if (err.message.includes("Error inesperado") || err.message.includes("del servidor")) {
        onError(err.message);
        throw err;
      }
      onError("El servidor devolvió una respuesta JSON inesperada");
      throw new Error("El servidor devolvió una respuesta JSON inesperada");
    }
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let doneReceived = false;

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split("\n\n");
      buffer = parts.pop() || "";

      for (const part of parts) {
        if (!part.trim()) continue;

        const lines = part.split("\n");
        let eventType = "";
        let data = "";

        for (const line of lines) {
          if (line.startsWith("event: ")) {
            eventType = line.slice(7).trim();
          } else if (line.startsWith("data: ")) {
            data = line.slice(6);
          }
        }

        // Si no hay líneas data:/event:, puede ser JSON plano sin formato SSE
        if (!data && !eventType && part.trim()) {
          try {
            const parsed = JSON.parse(part.trim());
            if (parsed.error) {
              onError(parsed.error);
              return;
            }
            if (parsed.text) {
              onChunk(parsed.text);
            }
          } catch {
            // Ignorar líneas mal formadas
          }
          continue;
        }

        if (!data) continue;

        try {
          const parsed = JSON.parse(data);
          // eslint-disable-next-line no-console
          console.log("[AI Stream] event:", eventType || "(text)", "data:", parsed);

          switch (eventType) {
            case "meta":
              onMeta(parsed);
              break;
            case "tool":
              onTool(parsed.tool);
              break;
            case "done":
              doneReceived = true;
              onDone({
                conversationId: parsed.conversationId,
                title: parsed.title,
              });
              break;
            case "error":
              onError(parsed.error);
              return;
            default:
              // Defensa: si el chunk tiene "error" en lugar de "text", tratarlo como error
              if (parsed.error && !parsed.text) {
                onError(parsed.error);
                return;
              }
              onChunk(parsed.text || "");
          }
        } catch {
          // Si no es JSON válido, podría ser texto plano del servidor
          if (data.toLowerCase().includes("gemini_api_key") || data.toLowerCase().includes("no está configurada")) {
            onError(data);
            return;
          }
        }
      }
    }

    // Si terminó el stream sin recibir "done", verificar si hay algo pendiente en el buffer
    if (!doneReceived && buffer.trim()) {
      try {
        const parsed = JSON.parse(buffer.trim());
        if (parsed.error) {
          onError(parsed.error);
        } else if (parsed.text) {
          onChunk(parsed.text);
        }
      } catch {
        // ignore
      }
    }
  } catch (err) {
    onError(err.message || "Error de conexión");
    throw err;
  }
}
